import { Event } from '@sentry/nextjs';
import { ExtraErrorData } from '@sentry/integrations';

const keys = [/password/i, /secret/i, /token/i, /credentials/i];

type EventProperty = Array<Record<string, any>> | Record<string, any>;

export function scrub(event?: EventProperty): void {
  if (typeof event !== 'object') return;

  if (Array.isArray(event)) return event.forEach((element) => scrub(element));

  for (const eventKey in event) {
    const isSensitiveKey = keys.some((key: any) => key.test(eventKey));

    if (isSensitiveKey) {
      delete event[eventKey];
    } else if (typeof event[eventKey] === 'object') {
      scrub(event[eventKey]);
    }
  }
}

export const removeSensitiveData = (event: Event) => {
  if (event.transaction && keys.some((key: any) => key.test(event.transaction)))
    event.transaction = 'redacted';

  scrub(event.breadcrumbs);
  scrub(event.request);
  scrub(event.contexts);

  scrub(event.transaction_info);
  scrub(event.user);
};

export const CLIENT_EXCEPTIONS_TO_IGNORE = [
  // Ignore http://bit.ly/2hk42Ap, resulting from what
  // appears to be a Chrome bug http://bit.ly/2gVbncS
  /__gCrWeb\.autofill\.extractForms/,

  // Ignore http://bit.ly/2hk71Zv, resulting from
  // some really strange issue in Safari 9. Experienced
  // by Mozilla team, too http://bit.ly/2hk9vHq
  /docs-homescreen-gb-container/,

  // Ignore http://bit.ly/2mVGcgX. It's a weird react error.
  // Google says it _might_ be a logging error? http://bit.ly/2lEB3ZW
  /e\.getHostNode/,

  // Ignore http://bit.ly/2lyZSao, resulting from
  // some Chrome plugin presumably.
  /ahk_main/,

  // Some weird firefox errors that seem to happen only on firefox for iOS.
  // https://bugzilla.mozilla.org/show_bug.cgi?id=1394296
  /__firefox__\./,

  /*
    This error occurs when a ResizeObserver event handler makes a size change
    to the element being observed, which causes the event handler to be
    triggered again, creating a loop. This error is thrown to prevent this
    potential loop.

    There is typically no noticeable effect to the user and
    most browsers don't even log this error to the console.

    The error message can differ per browser.
  */
  /ResizeObserver loop limit exceeded/,
  /ResizeObserver loop completed with undelivered notifications\./,

  /*
    This error occurs when an unfinished `fetch` request is cancelled, most
    often by navigating to a new page.

    The error message can differ per browser.
  */
  /TypeError: Load failed/,
  /TypeError: cancelled/,
];

export const SERVER_EXCEPTIONS_TO_IGNORE = [
  // Error emitted by Builder.io during build-time that can safely be ignored
  /Missing registration for/,

  // Error emitted until we remove v3 tracking package
  /This package has been deprecated. We strongly encourage if you are planning to use it for the first time, please use the new library Events-Toolkit-Browser: https:\/\/github.com\/zapier\/events-toolkit-js/,
  /Do not use the cta disclaimer with the zapier background/,
];

// Base configurations used by both client and server
export const BASE_CONFIG = {
  dsn: process.env.SENTRY_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN,
  // tracesSampleRate is the sampling of transactions sent to Sentry
  tracesSampleRate: 0.2,
  // sampleRate is the sampling rate of errors sent to Sentry
  sampleRate: 1.0,
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
  release: process.env.SENTRY_RELEASE,
  integrations: [
    // Extracts all non-native attributes from the Error object and attaches
    // them to the event as the `extra` data.
    // https://docs.sentry.io/platforms/javascript/#extraerrordata
    new ExtraErrorData(),
  ],
};
